import type { JSX } from 'react';
import type { FieldPath, FieldPathByValue, FieldPathValue, FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';
import type { ExtendTypeWith } from 'ts/commons/ExtendTypeWith';
import type { FormCheckboxProps } from 'ts/components/Form';
import { FormCheckbox } from 'ts/components/Form';

/** Props for HookFormCheckbox. */
type HookFormCheckboxProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = ExtendTypeWith<
	FormCheckboxProps,
	Pick<UseControllerProps<TFieldValues, TName>, 'name' | 'rules' | 'control'>
>;

/**
 * This is a wrapper around Form.Checkbox that is compatible with react-hook-form. The implementation assumes that the
 * react-hook-form exposed value is a boolean.
 */
export function HookFormCheckbox<
	TFieldValues extends FieldValues,
	TName extends FieldPathByValue<TFieldValues, boolean | undefined> = FieldPathByValue<
		TFieldValues,
		boolean | undefined
	>
>({ name, rules, control, ...checkboxProps }: HookFormCheckboxProps<TFieldValues, TName>): JSX.Element {
	const { field, fieldState } = useController<TFieldValues, TName>({ name, rules, control });
	return (
		<FormCheckbox
			name={name}
			onChange={(e, { checked }) => field.onChange(checked as FieldPathValue<TFieldValues, TName>)}
			checked={field.value}
			error={fieldState.error?.message || fieldState.invalid}
			{...checkboxProps}
		/>
	);
}
